import React from 'react';
import {
  FaShieldHalved,
  FaTriangleExclamation,
  FaUsers,
  FaClock,
  FaCircleCheck,
  FaCircleXmark,
} from 'react-icons/fa6';
import RiskGauge from './RiskGauge';
import RiskIndicatorChart from './RiskIndicatorChart';
import { translateIncidentType } from '../../utils/translateIncidentType';

const CHAIN_NAMES = {
  '-3': 'Solana',
  '-2': 'TRON',
  '-1': 'Bitcoin',
  1: 'Ethereum',
  10: 'Optimism',
  56: 'BSC',
  137: 'Polygon',
  42161: 'Arbitrum',
  43114: 'Avalanche',
  8453: 'Base',
};

const VERDICT_STYLES = {
  critical: 'from-red-500 to-rose-600 border-red-300 dark:border-red-700',
  high: 'from-orange-500 to-amber-600 border-orange-300 dark:border-orange-700',
  medium: 'from-yellow-500 to-amber-500 border-yellow-300 dark:border-yellow-700',
  normal: 'from-green-500 to-emerald-600 border-green-300 dark:border-green-700',
};

function formatFreshness(hoursAgo, t) {
  if (hoursAgo === null || hoursAgo === undefined) {
    return t('dataFreshnessUnknown');
  }
  if (hoursAgo < 1) return t('dataFreshnessJustNow');
  if (hoursAgo < 24) return t('dataFreshnessHours', { hours: hoursAgo });
  const days = Math.floor(hoursAgo / 24);
  return t('dataFreshnessDays', { days });
}

function SummaryCard({ icon: Icon, title, value, subtitle, accent }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-purple-200/50 dark:border-purple-700/30 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${accent}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function WalletRiskReport({ report, walletAddress, chainId, t }) {
  if (!report) return null;

  const { summary, riskLabels, sanctionStatus, complianceScore, platform, dataFreshness, generatedAt } = report;
  const verdictStyle = VERDICT_STYLES[summary?.verdict] || VERDICT_STYLES.normal;
  const attributes = riskLabels?.data?.attributes || [];
  const mainEntity = riskLabels?.data?.main_entity || t('unknown');
  const nameTag = riskLabels?.data?.name_tag || t('unknown');
  const chainName = CHAIN_NAMES[chainId] || `Chain ${chainId}`;

  return (
    <div className="space-y-6">
      <div className={`rounded-2xl border bg-gradient-to-r ${verdictStyle} p-[1px] shadow-lg`}>
        <div className="rounded-2xl bg-white dark:bg-gray-900 px-6 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-purple-500 dark:text-purple-300">
                {t('riskReportTitle')}
              </p>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {t(`verdict_${summary?.verdict || 'normal'}`)}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 break-all font-mono">
                {walletAddress}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {chainName} · {t('reportGeneratedAt')}: {new Date(generatedAt).toLocaleString()}
              </p>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <RiskGauge
                score={summary?.riskScore}
                riskLevel={summary?.riskLevel}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard
          icon={FaShieldHalved}
          title={t('sanctionCheckResult')}
          value={summary?.isSanctioned ? t('sanctioned') : t('walletNotSanctioned')}
          subtitle={formatFreshness(dataFreshness?.sanctionStatus?.hoursAgo, t)}
          accent="bg-red-500"
        />
        <SummaryCard
          icon={FaUsers}
          title={t('platformReports')}
          value={
            summary?.hasPlatformSignal
              ? t('platformReportCount', { count: summary.platformReportCount })
              : t('noReports')
          }
          subtitle={
            summary?.platformReporterCount > 0
              ? t('reportedBy') + ` ${summary.platformReporterCount} ` + t('users')
              : null
          }
          accent="bg-indigo-500"
        />
        <SummaryCard
          icon={FaTriangleExclamation}
          title={t('riskLabels')}
          value={attributes.length > 0 ? `${attributes.length} ${t('riskLabelCount')}` : t('noWalletTags')}
          subtitle={`${t('mainEntity')}: ${mainEntity}`}
          accent="bg-purple-500"
        />
        <SummaryCard
          icon={FaClock}
          title={t('dataFreshness')}
          value={formatFreshness(dataFreshness?.complianceScore?.hoursAgo, t)}
          subtitle={
            dataFreshness?.complianceScore?.cached
              ? t('dataFromCache')
              : t('dataFromLiveApi')
          }
          accent="bg-slate-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-purple-200/50 dark:border-purple-700/30 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            {t('complianceRiskScore')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('higherScoreHigherRisk')}</p>
          <RiskIndicatorChart
            indicators={complianceScore?.risk_indicators}
            t={t}
          />
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-xl border border-purple-200/50 dark:border-purple-700/30 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            {t('walletRiskDetails')}
          </h3>
          <div className="space-y-3 text-sm">
            <p>
              <span className="font-semibold text-gray-700 dark:text-gray-300">{t('mainEntity')}:</span>{' '}
              <span className="text-gray-900 dark:text-white">{mainEntity}</span>
            </p>
            <p>
              <span className="font-semibold text-gray-700 dark:text-gray-300">{t('nameTag')}:</span>{' '}
              <span className="text-gray-900 dark:text-white">{nameTag}</span>
            </p>
            {attributes.length > 0 ? (
              <ul className="space-y-2 mt-3">
                {attributes.map((attr, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900/60"
                  >
                    {attr.name?.includes('BLOCKED') ? (
                      <FaCircleXmark className="text-red-500 shrink-0" />
                    ) : (
                      <FaCircleCheck className="text-purple-500 shrink-0" />
                    )}
                    <span className={attr.name?.includes('BLOCKED') ? 'text-red-500 font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                      {attr.name?.includes('BLOCKED') ? t('blocked') : attr.name}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">{t('noWalletTagsAvailable')}</p>
            )}
          </div>
        </section>
      </div>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-purple-200/50 dark:border-purple-700/30 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          {t('sanctionCheckResult')}
        </h3>
        {sanctionStatus?.is_sanctioned ? (
          <div className="space-y-3">
            <p className="text-red-500 font-bold flex items-center gap-2">
              <FaCircleXmark /> {t('walletSanctioned')}
            </p>
            {(sanctionStatus.identifications || []).map((item, index) => (
              <div key={index} className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4 text-sm">
                <p><strong>{t('sanctionSource')}:</strong> {item.name}</p>
                {item.category && <p className="mt-1"><strong>{t('indicatorName')}:</strong> {item.category}</p>}
                {item.description && <p className="mt-1 text-gray-600 dark:text-gray-300">{item.description}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-green-600 dark:text-green-400 font-semibold flex items-center gap-2">
            <FaCircleCheck /> {t('walletNotSanctioned')}
          </p>
        )}
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-purple-200/50 dark:border-purple-700/30 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          {t('platformIntelligence')}
        </h3>
        {platform?.reports?.length > 0 ? (
          <div className="space-y-3">
            {platform.reports.map((item) => (
              <div
                key={item.id}
                className="rounded-lg border border-indigo-200/60 dark:border-indigo-800/60 bg-indigo-50/50 dark:bg-indigo-950/20 p-4"
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-xs font-semibold uppercase px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200">
                    {translateIncidentType(item.incidentType, t)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {item.source === 'imported' || item.source === 'chainabuse'
                      ? t('importedReport')
                      : t('communityReport')}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(item.reportedAt).toLocaleDateString()}
                  </span>
                </div>
                {item.description && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{item.description}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">{t('noPlatformReports')}</p>
        )}
        {platform?.blacklistEntry && (
          <div className="mt-4 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/20 p-4 text-sm">
            <p className="font-semibold text-orange-700 dark:text-orange-300">{t('reportedAsSuspicious')}</p>
            {platform.blacklistEntry.latestComment && (
              <p className="mt-1 text-gray-600 dark:text-gray-300">{platform.blacklistEntry.latestComment}</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default WalletRiskReport;
